import { FC, FormEvent, useState, useRef } from 'react';

import useActions from '@app/store/hooks/useActions';
import { useLoginWithGoogleMutation } from '@entities/auth';
import { TUserRoles } from '@entities/user';
import { FormItemWithError } from '@shared/components/Form';
import Loading from '@shared/components/Loading';

import '../auth.css';
import { LoginWithGoogle } from './LoginWithGoogle';
import useLoginFormValidation from '../hooks/useLoginFormValidation';

interface ILoginFormProps {
  role: TUserRoles;
  mutation: any; // eslint-disable-line
}

export const LoginForm: FC<ILoginFormProps> = ({ role, mutation }) => {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [as, setAs] = useState('admin');

  const [login, { isLoading: isLoginLoading, isError: isRequestError, error: requestError }] =
    mutation();
  const [loginWithGoogle, { isLoading: isGoogleLoading }] = useLoginWithGoogleMutation();
  const { setToken } = useActions();

  const { isError: clientIsError, errors: clientError, validateFields } = useLoginFormValidation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = emailInputRef.current?.value;
    const password = passwordInputRef.current?.value;

    if (!validateFields(email, password)) {
      return;
    }

    const loginData = role === 'admin' ? { email, password, as } : { email, password };

    try {
      const result = await login(loginData);

      if ('data' in result) {
        result.data.token && setToken(result.data);
      }
    } catch (err) {
      console.error('Caught error:', err);
    }
  };

  const handleGoogleLogin = async () => {
    const selectedRole = role === 'admin' ? as : role;

    try {
      const data = await loginWithGoogle({ as: selectedRole }).unwrap();

      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      }
    } catch (err) {
      console.error('Google login error:', err);
    }
  };

  const isLoading = isLoginLoading || isGoogleLoading;
  const isError = isRequestError || clientIsError;
  const error = isRequestError
    ? requestError
    : {
        status: 422,
        data: { errors: clientError, message: 'Please fill in all required fields.' },
      };

  return (
    <>
      {isLoading && <Loading />}

      <div className='login-form-block'>
        <h1>
          <span className='login-form-title'>{role}</span> login | Backoffice
        </h1>
        <form onSubmit={handleSubmit} action='/auth/backoffice' className='login-form'>
          <FormItemWithError name='email' label='Email' isError={isError} error={error}>
            <input
              autoComplete='email'
              type='email'
              name='email'
              id='email'
              ref={emailInputRef}
              placeholder='example@mail.com'
            />
          </FormItemWithError>

          <FormItemWithError name='password' label='Password' isError={isError} error={error}>
            <input
              autoComplete='current-password'
              type='password'
              name='password'
              id='password'
              ref={passwordInputRef}
            />
          </FormItemWithError>

          {role === 'admin' && (
            <FormItemWithError name='as' label='As' isError={isError} error={error}>
              <select name='as' id='as' value={as} onChange={e => setAs(e.target.value)}>
                <option value='admin'>Admin</option>
                <option value='operator'>Operator</option>
              </select>
            </FormItemWithError>
          )}

          <div className='form-submit'>
            <button type='submit' disabled={isLoading}>
              Log in
            </button>
          </div>
        </form>

        {role === 'admin' && (
          <div className='form-submit google-login-button'>
            <LoginWithGoogle onClick={handleGoogleLogin} />
          </div>
        )}
      </div>
    </>
  );
};
